"use client";

import { useState, useEffect } from "react";
import {
  X,
  Building2,
  Mail,
  Phone,
  User,
  Send,
  CheckCircle,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { postSolicitudCarga } from "@/lib/api/datosGenerales/postSolicitudCarga";
import type { SolicitudCarga } from "@/lib/api/datosGenerales/types/solicitudCarga";
import { fetchTipoEmpresa } from "@/lib/api/datosGenerales/fetchTipoEmpresa";
import type { TipoEmpresa } from "@/lib/api/datosGenerales/types/tipoEmpresa";

interface FormaParteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormDataExtender {
  nombre: string;
  email: string;
  telefono: string;
  empresa_tipo: string;
  nombreEmpresa: string;
}

export default function FormaParteModal({
  isOpen,
  onClose,
}: FormaParteModalProps) {
  const [formData, setFormData] = useState<FormDataExtender>({
    empresa_tipo: "",
    telefono: "",
    nombre: "",
    email: "",
    nombreEmpresa: "",
  });
  const t = useTranslations("carga.form");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState("");
  const [tiposEmpresas, setTiposEmpresas] = useState<TipoEmpresa[]>([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      fetchTipoEmpresa()
        .then((tipos) => setTiposEmpresas(tipos))
        .catch((err) => console.error("Error fetching tipoEmpresa:", err));
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      // Validación básica
      if (
        // !formData.empresa_tipo ||
        !formData.telefono ||
        !formData.nombre ||
        !formData.email
      ) {
        setError("Por favor complete todos los campos obligatorios");
        setIsSubmitting(false);
        return;
      }

      const solicitudData: SolicitudCarga = {
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        empresa_tipo: formData.empresa_tipo || "no_especificado",
        nombre_empresa: formData.nombreEmpresa,
      };

      // Aquí irá la llamada al API para enviar el correo
      const response = await postSolicitudCarga(solicitudData);

      if (!response.success) {
        throw new Error(response.message || "Error al enviar el formulario");
      }

      setSubmitSuccess(true);

      // Cerrar el modal después de 3 segundos
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setError(
        "Hubo un error al enviar el formulario. Por favor intente nuevamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      empresa_tipo: "",
      telefono: "",
      nombre: "",
      email: "",
      nombreEmpresa: "",
    });
    setSubmitSuccess(false);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pt-20 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[75vh] overflow-y-auto animate-slideUp">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-2xl shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {t("title")}
              </h2>
              <p className="text-blue-100 text-sm md:text-base">{t("desc")}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
              aria-label="Cerrar modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {submitSuccess ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 animate-bounce">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {t("solicitudEnviada")}
              </h3>
              <p className="text-gray-600 mb-2">{t("hemosRecibido")}</p>
              <p className="text-gray-600">{t("nuestroEquipo")}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nombre de Empresa */}
              <div>
                <label
                  htmlFor="nombreEmpresa"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("name")} <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    id="nombreEmpresa"
                    value={formData.nombreEmpresa}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nombreEmpresa: e.target.value,
                      })
                    }
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Ej: Logistics Corp"
                  />
                </div>
              </div>

              {/* Tipo de Empresa */}
              <div>
                <label
                  htmlFor="tipoEmpresa"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("tipo")} <span className="text-red-600">*</span>
                </label>
                <select
                  id="tipoEmpresa"
                  value={formData.empresa_tipo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      empresa_tipo: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">{t("seleccioneOpcion")}</option>
                  {tiposEmpresas.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nombre de Persona */}
              <div>
                <label
                  htmlFor="nombrePersona"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("contacto")} <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    id="nombrePersona"
                    required
                    value={formData.nombre}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nombre: e.target.value,
                      })
                    }
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Ej: Juan Pérez"
                  />
                </div>
              </div>

              {/* Grid para Teléfono y Correo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Número de Contacto */}
                <div>
                  <label
                    htmlFor="numeroContacto"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    {t("phone")} <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="tel"
                      id="numeroContacto"
                      required
                      value={formData.telefono}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          telefono: e.target.value,
                        })
                      }
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="+507 1234-5678"
                    />
                  </div>
                </div>

                {/* Correo */}
                <div>
                  <label
                    htmlFor="correo"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    {t("correo")} <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      id="correo"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="info@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t("enviando")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t("enviar")}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
